"use client";

import { createContext, useEffect, useState } from "react";
import { Center, Spinner } from "@chakra-ui/react";
import { usePostHog } from "posthog-js/react";
import { GetApplicationDetailsById } from "../actions/application/GetApplicationDetailsById";
import moment from "moment";
import handleServerAction from "@/lib/hooks/handleServerAction";
import { GetTemplateCount } from "@/actions/common/GetTemplateCount";
import { GetIntakeQuestionnaire } from "@/actions/intake/GetIntakeQuestionnaire";
import { GetSSAForm } from "@/actions/application/GetForm";
import { buildInitialApplication } from "@/utils/shared";
import { GetChildrenInformation } from "@/actions/templates/ChildrenInformation/GetChildrenInformation";
import { GetMarriageInfo } from "@/actions/templates/MarriageInfo/GetMarriageInfo";
import { GetMedicalHistory } from "@/actions/templates/MedicalHistory/GetMedicalHistory";
import { GetEmergencyContact } from "@/actions/templates/EmergencyContact/GetEmergencyContact";
import { GetMedicationDetails } from "@/actions/templates/MedicationDetails/GetMedicationDetails";
import { GetWorkHistory } from "@/actions/templates/WorkHistory/GetWorkHistory";
import { PENDING_DATA_COLLECTION_APP_STATUSES } from "@/lib/constants";

const AuthContext = createContext<{
  userDetails: any;
  getUserDetails: () => void;
}>({
  userDetails: null,
  getUserDetails: () => {},
});

const AuthProvider = ({
  children,
  user,
}: {
  children: React.ReactNode;
  user: any;
}) => {
  const posthog = usePostHog();
  const [userDetails, setUserDetails] = useState();

  const getUserDetails = async () => {
    const user_details = await handleServerAction(GetApplicationDetailsById);

    const intake_details =
      (await handleServerAction(GetIntakeQuestionnaire).catch((err) => {
        // 404 not found
        console.error(err);
      })) || {};

    const ssa_response =
      (await handleServerAction(GetSSAForm).catch((err) => {
        // 404 not found
        console.error(err);
      })) || {};

    const ssa_details = ssa_response.json ? JSON.parse(ssa_response?.json) : {};

    const template_count = await handleServerAction(GetTemplateCount);

    let children_templates = [],
      marriage_templates = [],
      doctor_templates = [],
      contact_templates = [],
      medication_templates = [],
      work_templates = [];

    if (template_count.childrenInfoCount) {
      children_templates = await handleServerAction(GetChildrenInformation);
    }

    if (template_count.marriageInfoCount) {
      marriage_templates = await handleServerAction(GetMarriageInfo);
    }

    if (template_count.doctorVisitsCount) {
      doctor_templates = await handleServerAction(GetMedicalHistory);
    }

    if (template_count.emergencyContactCount) {
      contact_templates = await handleServerAction(GetEmergencyContact);
    }

    if (template_count.medicationDetailsCount) {
      medication_templates = await handleServerAction(GetMedicationDetails);
    }

    if (template_count.workHistoryCount) {
      work_templates = await handleServerAction(GetWorkHistory);
    }

    const current_marriage = marriage_templates.find(
      (marriage: any) => !marriage.dateOfDivorce
    );

    const prior_marriages = marriage_templates.filter(
      (marriage: any) => marriage.dateOfDivorce
    );

    const templates = {
      current_marriage: current_marriage ? 1 : 0,
      prior_marriages: prior_marriages.length,
      children: children_templates.length,
      employment: work_templates.length,
      medications: medication_templates.length,
      doctor_visits: doctor_templates.length,
      contacts: contact_templates.length,
    };

    const defaultApplicationValues = {
      ...buildInitialApplication(user_details, intake_details, {
        children: children_templates,
        marriage: marriage_templates,
        medications: medication_templates,
        emergency: contact_templates,
        work: work_templates,
        doctors: doctor_templates,
        contacts: contact_templates,
      }),
      ...ssa_details,
      templates,
      gender: user_details.gender,
    };

    setUserDetails((oldDetails: any) => {
      return {
        ...oldDetails, // Keep old details that may have been set on the client (such as isJailUser and isJailsso)
        ...user_details,
        pendingDataCollection:
          user_details.isJailUser ||
          PENDING_DATA_COLLECTION_APP_STATUSES.includes(
            user_details.applicationStatus
          ),
        isJailUser: user.isJailUser,
        isJailSso: user.isJailSso,
        application: defaultApplicationValues,
      };
    });
  };

  if (user) {
    posthog.identify(user.mindsetUserId, {
      is_low_vision: user.isLowVision,
      is_disabled: user.isDisabled,
      is_married: user.isMarried,
      is_jail_user: user.isJailUser ? true : false,
      age: moment().diff(user.dateOfBirth, "years"),
      application_status: user.applicationStatus,
      docusign_status: user.powerform_url.envelopStatus,
    });
    // Overwrite/merge current profiles
    posthog.alias(user.mindsetUserId, user.id);
  }

  useEffect(() => {
    getUserDetails();
  }, []);

  return userDetails ? (
    <AuthContext.Provider
      value={{
        userDetails,
        getUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  ) : (
    <Center minH="100vh">
      <Spinner color="brand.primary" />
    </Center>
  );
};

export { AuthContext, AuthProvider };
